import React from 'react'
import {Button, ButtonGroup} from '@mui/material'
import {useTheme} from '@mui/material/styles'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import {DatePicker} from '@mui/x-date-pickers/DatePicker'
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import {heIL, enUS} from '@mui/x-date-pickers/locales'
import dayjs from 'dayjs'
import 'dayjs/locale/he'
import 'dayjs/locale/en'
import {useTranslation} from 'react-i18next'
import {useIsRtl} from 'store/Store'
import storage from 'services/storage'

function ButtonField(props) {
    const {setOpen, label, id, disabled, InputProps: {ref} = {}, inputProps: {'aria-label': ariaLabel} = {}} = props
    return (
        <Button
            id={id}
            disabled={disabled}
            ref={ref}
            aria-label={ariaLabel}
            onClick={() => setOpen?.((prev) => !prev)}
            sx={{
                minWidth: 110,
                borderRadius: 0,
                fontWeight: 600,
                fontSize: 13,
                px: 2,
                whiteSpace: 'nowrap',
                textTransform: 'none',
            }}>
            {label}
        </Button>
    )
}

function ButtonDatePicker(props) {
    const [open, setOpen] = React.useState(false)
    return (
        <DatePicker
            slots={{...props.slots, field: ButtonField}}
            slotProps={{...props.slotProps, field: {setOpen}}}
            {...props}
            open={open}
            onClose={() => setOpen(false)}
            onOpen={() => setOpen(true)}
        />
    )
}

/**
 * DayNavigator
 *
 * Props:
 *   date     {dayjs}  – currently selected date
 *   onChange {fn}     – called with new dayjs date
 *   maxDate  {dayjs}  – upper bound (defaults to today)
 */
const DayNavigator = ({date, onChange, maxDate}) => {
    const {t} = useTranslation()
    const theme = useTheme()
    const isRtl = useIsRtl()

    const currentLanguage = storage.getLanguage() || 'he'
    dayjs.locale(currentLanguage)
    const localeMap = {he: heIL, en: enUS}
    const currentLocale = localeMap[currentLanguage] || localeMap.he

    const ceiling = maxDate ?? dayjs().startOf('day')
    const isAtMax = !date || date.startOf('day').isSame(ceiling, 'day')
    const isToday = date && date.startOf('day').isSame(dayjs().startOf('day'), 'day')

    const handlePrev = () => onChange(date.subtract(1, 'day'))
    const handleNext = () => onChange(date.add(1, 'day'))

    let label = ''
    if (isToday) {
        label = t('common.today')
    } else if (date) {
        label = date.format('DD MMM YYYY')
    }

    // In RTL the ButtonGroup renders children right-to-left,
    // so ChevronLeft visually appears on the left (= forward/newer in RTL).
    // Swap icons so directional meaning stays correct.
    const PrevIcon = isRtl ? ChevronRightIcon : ChevronLeftIcon
    const NextIcon = isRtl ? ChevronLeftIcon : ChevronRightIcon

    const primaryColor = isToday ? theme.palette.primary.main : undefined

    return (
        <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={currentLanguage}
            localeText={currentLocale.components.MuiLocalizationProvider.defaultProps.localeText}>
            <ButtonGroup variant="outlined" size="small" disableElevation>
                <Button onClick={handlePrev} sx={{px: 0.75}}>
                    <PrevIcon sx={{fontSize: 18}} />
                </Button>

                <ButtonDatePicker
                    value={date}
                    onChange={(newDate) => newDate && onChange(newDate)}
                    maxDate={ceiling}
                    label={label}
                    slotProps={{
                        openPickerButton: {size: 'small'},
                        textField: {size: 'small'},
                        day: {
                            sx: {'&.Mui-selected': {fontWeight: 700}},
                        },
                    }}
                    sx={{'& button': {color: primaryColor}}}
                />

                <Button onClick={handleNext} disabled={isAtMax} sx={{px: 0.75}}>
                    <NextIcon sx={{fontSize: 18}} />
                </Button>
            </ButtonGroup>
        </LocalizationProvider>
    )
}

export default DayNavigator
